// Backend/cms/src/pages/system/HelpSupport.tsx
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Dialog from '../../components/ui/Dialog';
import { helpSupportService } from '../../services/help-support.service';
import { ContactInfo, FaqItem } from '../../types/help-support';
import { getErrorMsg } from '../../utils/error';

const EMPTY_CONTACT: ContactInfo = { whatsapp: '', supportEmail: '', phone: '' };
const EMPTY_FAQ = { question: '', answer: '', sortOrder: 0, isActive: true };

const HelpSupport = () => {
  const { t } = useTranslation('common');

  const [contact, setContact]     = useState<ContactInfo>(EMPTY_CONTACT);
  const [faqs, setFaqs]           = useState<FaqItem[]>([]);
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [success, setSuccess]     = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editFaq, setEditFaq]     = useState<FaqItem | null>(null);
  const [faqForm, setFaqForm]     = useState(EMPTY_FAQ);
  const [faqSaving, setFaqSaving] = useState(false);
  const [deleteId, setDeleteId]   = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [c, f] = await Promise.all([
        helpSupportService.getContact(),
        helpSupportService.listFaqs(),
      ]);
      setContact(c);
      setFaqs(f.data);
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to load data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const saveContact = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await helpSupportService.updateContact(contact);
      setContact(updated);
      setSuccess('Contact info saved.');
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to save contact info'));
    } finally {
      setSaving(false);
    }
  };

  const openCreate = () => {
    setEditFaq(null);
    setFaqForm(EMPTY_FAQ);
    setModalOpen(true);
  };

  const openEdit = (faq: FaqItem) => {
    setEditFaq(faq);
    setFaqForm({ question: faq.question, answer: faq.answer, sortOrder: faq.sortOrder, isActive: faq.isActive });
    setModalOpen(true);
  };

  const saveFaq = async () => {
    setFaqSaving(true);
    setError('');
    setSuccess('');
    try {
      if (editFaq) {
        await helpSupportService.updateFaq(editFaq.id, faqForm);
      } else {
        await helpSupportService.createFaq(faqForm);
      }
      setModalOpen(false);
      await refresh();
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to save FAQ'));
    } finally {
      setFaqSaving(false);
    }
  };

  const confirmDelete = async () => {
    setError('');
    setSuccess('');
    if (!deleteId) return;
    try {
      await helpSupportService.deleteFaq(deleteId);
      setDeleteId(null);
      await refresh();
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to delete FAQ'));
    }
  };

  const moveOrder = async (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= faqs.length) return;
    const updated = faqs.map((f, i) => {
      if (i === index) return { ...faqs[swapIndex] };
      if (i === swapIndex) return { ...faqs[index] };
      return f;
    });
    try {
      await helpSupportService.reorderFaqs(
        updated.map((f, i) => ({ id: f.id, sortOrder: i })),
      );
      setFaqs(updated);
    } catch (e) {
      setError(getErrorMsg(e, 'Failed to reorder FAQs'));
    }
  };

  return (
    <Layout title={t('help_support')}>
      <div className="space-y-6">
        {error && (
          <div className="p-3 rounded-md bg-red-50 text-red-600 border border-red-100">{error}</div>
        )}
        {success && (
          <div className="p-3 rounded-md bg-green-50 text-green-600 border border-green-100">{success}</div>
        )}

        {/* Contact Info */}
        <Card className="p-5 space-y-4">
          <h2 className="text-base font-semibold">{t('contact_info')}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {(
              [
                { key: 'whatsapp',     label: t('whatsapp_number') },
                { key: 'supportEmail', label: t('support_email') },
                { key: 'phone',        label: t('phone_number') },
              ] as Array<{ key: keyof ContactInfo; label: string }>
            ).map(({ key, label }) => (
              <div key={key}>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {label}
                </label>
                <input
                  type="text"
                  value={contact[key]}
                  onChange={(e) => setContact((c) => ({ ...c, [key]: e.target.value }))}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
                />
              </div>
            ))}
          </div>
          <Button variant="primary" onClick={saveContact} disabled={saving || loading}>
            {saving ? t('saving') : t('save')}
          </Button>
        </Card>

        {/* FAQs */}
        <Card className="p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold">{t('faqs')}</h2>
            <Button variant="primary" onClick={openCreate}>{t('add_faq')}</Button>
          </div>
          {loading ? (
            <p className="text-sm text-gray-500">{t('loading')}</p>
          ) : faqs.length === 0 ? (
            <p className="text-sm text-gray-500">{t('noData')}</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                  <th className="pb-2 font-medium">{t('faq_question')}</th>
                  <th className="pb-2 font-medium w-20">{t('faq_sort_order')}</th>
                  <th className="pb-2 font-medium w-20">{t('faq_active')}</th>
                  <th className="pb-2 font-medium w-28">{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {faqs.map((faq, idx) => (
                  <tr key={faq.id} className="border-b dark:border-gray-700">
                    <td className="py-2 pr-4 max-w-xs truncate">{faq.question}</td>
                    <td className="py-2">
                      <div className="flex gap-1">
                        <button
                          onClick={() => moveOrder(idx, 'up')}
                          disabled={idx === 0}
                          aria-label={t('move_up')}
                          className="px-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                        >↑</button>
                        <button
                          onClick={() => moveOrder(idx, 'down')}
                          disabled={idx === faqs.length - 1}
                          aria-label={t('move_down')}
                          className="px-1 text-gray-400 hover:text-gray-700 disabled:opacity-30"
                        >↓</button>
                      </div>
                    </td>
                    <td className="py-2">{faq.isActive ? '✓' : '—'}</td>
                    <td className="py-2">
                      <div className="flex gap-2">
                        <button onClick={() => openEdit(faq)} className="text-blue-600 hover:underline text-xs">{t('edit')}</button>
                        <button onClick={() => setDeleteId(faq.id)} className="text-red-500 hover:underline text-xs">{t('delete')}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* FAQ Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editFaq ? t('edit_faq') : t('add_faq')}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">{t('faq_question')}</label>
            <textarea
              rows={2}
              value={faqForm.question}
              onChange={(e) => setFaqForm((f) => ({ ...f, question: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">{t('faq_answer')}</label>
            <textarea
              rows={4}
              value={faqForm.answer}
              onChange={(e) => setFaqForm((f) => ({ ...f, answer: e.target.value }))}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:text-white"
            />
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={faqForm.isActive}
                onChange={(e) => setFaqForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              {t('faq_active')}
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setModalOpen(false)}>{t('cancel')}</Button>
            <Button variant="primary" onClick={saveFaq} disabled={faqSaving || !faqForm.question || !faqForm.answer}>
              {faqSaving ? t('saving') : t('save')}
            </Button>
          </div>
        </div>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        title={t('delete')}
      >
        <div className="space-y-4">
          <p className="text-sm">{t('delete_faq_confirm')}</p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDeleteId(null)}>{t('cancel')}</Button>
            <Button variant="danger" onClick={confirmDelete}>{t('delete')}</Button>
          </div>
        </div>
      </Dialog>
    </Layout>
  );
};

export default HelpSupport;
